import { apiFetch } from "../../../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

/**
 * 🔥 API Service Layer - جميع API calls
 * يحتوي على دوال جلب البيانات فقط بدون state management
 */

// ============== EMPLOYEE COUNT ==============
export const fetchEmployeeCountAPI = async (clientName) => {
  clientName = clientName ?? localStorage.getItem("clientName");
  const response = await apiFetch(`${API_URL}/employees/clients/count`, { method: 'GET' });
  const data = await response.json();

  let totalCount = 0;
  if (clientName && clientName.toLowerCase() !== "infinity") {
    const filteredData = data.filter(c => c.name?.toLowerCase() === clientName.toLowerCase());
    totalCount = filteredData.reduce((sum, c) => sum + (c.count || 0), 0);
  } else {
    totalCount = (data || []).reduce((sum, c) => sum + (c.count || 0), 0);
  }
  return totalCount;
};

// ============== EMPLOYEE COUNTS BY STATUS ==============
export const fetchEmployeeCountsAPI = async (viewType, clientName) => {
  clientName = clientName ?? localStorage.getItem("clientName");

  const [activeRes, onLeaveRes, archivedRes, empRes] = await Promise.all([
    apiFetch(`${API_URL}/employees/clients/Active/count`, { method: 'GET' }),
    apiFetch(`${API_URL}/employees/clients/OnLeave/count`, { method: 'GET' }),
    apiFetch(`${API_URL}/employees/archived`, { method: 'GET' }),
    apiFetch(`${API_URL}/employees/visibility?clientName=${encodeURIComponent(clientName || '')}&viewType=${viewType}`, { method: 'GET' }),
  ]);

  const activeData = await activeRes.json();
  const onLeaveData = await onLeaveRes.json();
  const archivedRaw = await archivedRes.json();
  const empRaw = await empRes.json();

  // Filter by client
  let clientActiveItems = activeData;
  let clientOnLeaveItems = onLeaveData;

  if (clientName && clientName.toLowerCase() !== "infinity") {
    clientActiveItems = activeData.filter(item => item?.name?.toLowerCase() === clientName.toLowerCase());
    clientOnLeaveItems = onLeaveData.filter(item => item?.name?.toLowerCase() === clientName.toLowerCase());
  }

  const activeCount = (clientActiveItems || []).reduce((sum, item) => sum + (item.count || 0), 0);
  const onLeaveCount = (clientOnLeaveItems || []).reduce((sum, item) => sum + (item.count || 0), 0);

  // Archived count
  const archivedList = Array.isArray(archivedRaw) ? archivedRaw : archivedRaw.employees || [];
  const filteredArchived = (clientName && clientName.toLowerCase() !== 'infinity')
    ? archivedList.filter(emp => emp.clientName?.toLowerCase() === clientName.toLowerCase())
    : archivedList;
  const archivedCount = (filteredArchived || []).length;

  // New employees (last 30 days)
  const empList = empRaw.employees || empRaw || [];
  const now = Date.now();
  const newEmployeesCount = (empList || []).filter(emp => {
    const created = emp.created_at || emp.createdAt || emp.fullEmployeeData?.created_at || emp.fullEmployeeData?.createdAt;
    if (!created) return false;
    const d = new Date(created);
    if (isNaN(d.getTime())) return false;
    const days = (now - d.getTime()) / (1000 * 60 * 60 * 24);
    return days <= 30;
  }).length;

  return {
    Active: activeCount,
    OnLeave: onLeaveCount,
    Recent: 0,
    archived: archivedCount,
    newEmployees: newEmployeesCount,
    Total: activeCount + onLeaveCount + archivedCount,
  };
};

// ============== EMPLOYEES ==============
export const fetchEmployeesAPI = async (viewType, clientName) => {
  clientName = clientName ?? localStorage.getItem("clientName");
  const response = await apiFetch(
    `${API_URL}/employees/visibility?clientName=${encodeURIComponent(clientName)}&viewType=${viewType}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    const body = await response.text().catch(() => "<unable to parse>");
    throw new Error(`fetchEmployeesAPI failed: ${response.status} ${body}`);
  }
  const responseData = await response.json();
  return responseData.employees || responseData || [];
};

// ============== ARCHIVED EMPLOYEES ==============
export const fetchArchivedEmployeesAPI = async (clientName) => {
  clientName = clientName ?? localStorage.getItem("clientName");
  const response = await apiFetch(`${API_URL}/employees/archived`, { method: 'GET' });
  const responseData = await response.json();
  const archivedData = Array.isArray(responseData) ? responseData : responseData.employees || [];

  let filteredArchivedData = archivedData;
  if (clientName && clientName.toLowerCase() !== "infinity") {
    filteredArchivedData = archivedData.filter(emp => emp.clientName?.toLowerCase() === clientName.toLowerCase());
  }

  return filteredArchivedData;
};

// ============== COMPANIES ==============
export const fetchCompaniesAPI = async (viewType, clientName) => {
  clientName = clientName ?? localStorage.getItem("clientName");
  const response = await apiFetch(
    `${API_URL}/employees/visibility?clientName=${encodeURIComponent(clientName)}&viewType=${viewType}`,
    { method: 'GET' }
  );
  const responseData = await response.json();
  const employeesData = responseData.employees || responseData || [];
  const uniqueCompanies = [...new Set((employeesData || []).map(emp => emp.workingCompany).filter(Boolean))].sort();
  return uniqueCompanies;
};

// ============== CLIENTS ==============
export const fetchClientsAPI = async () => {
  try {
    let response = await apiFetch(`${API_URL}/employees/clients/all`, { method: 'GET' });
    let data = response?.clients || response?.data || response || [];
    
    if (!Array.isArray(data) || data.length === 0) {
      const countRes = await apiFetch(`${API_URL}/employees/clients/count`, { method: 'GET' });
      data = Array.isArray(countRes) ? countRes : countRes?.data || countRes?.clients || [];
    }

    const map = {};
    const list = [];
    (data || []).forEach((c) => {
      const name = (c.name || c.clientName || c.client || "").toString();
      const code = c.code || c.clientCode || c.client_id || c.id || null;
      if (name) {
        map[name.toLowerCase()] = code;
        list.push({ name, code });
      }
    });

    return { map, list };
  } catch (err) {
    console.error('❌ Failed to fetch clients:', err);
    return { map: {}, list: [] };
  }
};

// ============== PROFILE PICTURE ==============
export const fetchEmployeeProfilePicAPI = async (infinityCode, picCacheRef, loadingPics, setLoadingPics) => {
  if (picCacheRef.current.has(infinityCode)) {
    return picCacheRef.current.get(infinityCode);
  }
  if (loadingPics.has(infinityCode)) {
    return null;
  }

  setLoadingPics(prev => new Set([...prev, infinityCode]));

  try {
    const response = await apiFetch(`${API_URL}/employees/pic/${infinityCode}`, { method: 'GET' });
    if (response.ok) {
      const blob = await response.blob();
      if (blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);
        picCacheRef.current.set(infinityCode, url);
        setLoadingPics(prev => { const newSet = new Set(prev); newSet.delete(infinityCode); return newSet; });
        return url;
      }
    }
    
    setLoadingPics(prev => { const newSet = new Set(prev); newSet.delete(infinityCode); return newSet; });
    return null;
  } catch (err) {
    console.error(`❌ Error loading pic for ${infinityCode}:`, err.message || err);
    setLoadingPics(prev => { const newSet = new Set(prev); newSet.delete(infinityCode); return newSet; });
    return null;
  }
};

// ============== ADD EMPLOYEE ==============
/**
 * Maps form fields to database schema fields and applies transformations
 * @param {Object} formData - Data from the form
 * @returns {Object} - Data ready for the database
 */
export const mapFormDataToDatabase = (formData) => {
  // ──────────────────────────────────────────────
  // Calculate age from DOB
  // ──────────────────────────────────────────────
  let age = null;
  if (formData.dateOfBirth) {
    const dob = new Date(formData.dateOfBirth);
    const today = new Date();
    age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
  }

  // ──────────────────────────────────────────────
  // Map form field names to database field names
  // ──────────────────────────────────────────────
  const dbPayload = {
    sr: formData.sr || formData.name || null,
    arabicName: formData.arabicName || null,
    email: formData.email || null,
    phoneNumber: formData.phone || null,                    // ✅ phone → phoneNumber
    nationalId: formData.nationalId || null,
    idRegion: formData.idRegion || null,
    departmentId: formData.departmentId ?? null,           // ✅ first-class department id
    position: formData.position ?? null,                   // ✅ job title string
    location: formData.location || formData.city || null,  // ✅ explicit location, else city
    dob: formData.dateOfBirth || null,                      // ✅ dateOfBirth → dob
    age: age,                                                // ✅ Calculated age
    gender: formData.gender || null,
    emergencyPhoneNumber: formData.emergencyPhone || null,
    hiringDate: formData.hiringDate || null,
    maritalStatus: formData.maritalStatus || null,
    nationality: formData.nationality || null,
    religion: formData.religion || null,
    militaryStatus: formData.militaryStatus || null,
    employeeState: formData.status || null,                 // ✅ status → employeeState
    graduateInstitution: formData.graduateInstitution || null,
    major: formData.major || null,
    graduationYear: formData.graduationYear || null,
    insuredBeginningDate: formData.insuredBeginningDate || null,
    insuranceStatus: formData.insuranceStatus || null,
    monthsForInsurance: formData.monthsForInsurance || null,
    insuranceNumber: formData.insuranceNumber || null,
    insuranceSalary: formData.insuranceSalary || null,
    medicalInsuranceStage: formData.medicalInsuranceStage || null,
    medicalNumber: formData.medicalNumber || null,
    uniformSize: formData.uniformSize || null,
    drivingLicenseDegree: formData.drivingLicenseDegree || null,
    drivingLicenseExpiry: formData.drivingLicenseExpiry || null,
    drivingLicenseStillValid: Boolean(formData.drivingLicenseStillValid),
    accountNumber: formData.accountNumber || null,
    city: formData.city || null,
    delivered: Boolean(formData.delivered),
    healthCertificateOB: Boolean(formData.healthCertificateOB),
    healthCertExpiryDate: formData.healthCertExpiryDate || null,
    healthCertStillValid: Boolean(formData.healthCertStillValid),
    idCopy: Boolean(formData.idCopy),
    gradCertificateOriginal: Boolean(formData.gradCertificateOriginal),
    birthCertificateCopy: Boolean(formData.birthCertificateCopy),
    militaryServiceCertificate: Boolean(formData.militaryServiceCertificate),
    laborLetterOriginal: Boolean(formData.laborLetterOriginal),
    noCrimeCertificate: Boolean(formData.noCrimeCertificate),
    photosCount: Boolean(formData.photosCount),
    formNo6Original: Boolean(formData.formNo6Original),
    medicalCheckForm111: Boolean(formData.medicalCheckForm111),
    didMedicalCheck: Boolean(formData.didMedicalCheck),
    medicalReportOriginal: Boolean(formData.medicalReportOriginal),
    lawyerCertificateCopy: Boolean(formData.lawyerCertificateCopy),
    driversLicenseCopy: Boolean(formData.driversLicenseCopy),
    testEngineerCertificate: Boolean(formData.testEngineerCertificate),
    accountantCertificate: Boolean(formData.accountantCertificate),
    laborMode: formData.laborMode || null,
    workingCompany: formData.workingCompany || null,
    contractCompany: formData.contractCompany || null,
    contractDate: formData.contractDate || null,
    contractExpiration: formData.contractExpiration || null,
    termOfContract: formData.termOfContract || null,
    lastContractDate: formData.lastContractDate || null,
    lastContractTerm: formData.lastContractTerm || null,
    lastContractExpiration: formData.lastContractExpiration || null,
    beRegularDate: formData.beRegularDate || null,
    toBeRegular: formData.toBeRegular || null,
    periodForBeingRegular: formData.periodForBeingRegular || null,
    address: formData.address || null,
    arabicAddress: formData.arabicAddress || null,
    country: formData.country || null,
    transportationAllowances: formData.transportationAllowances || null,
    variableSalary: formData.variableSalary || null,
    netSalary: formData.salary || formData.netSalary || null,
    firstLineManagerInfinityCode: formData.firstLineManagerCode || null,
    firstLineManagerName: formData.firstLineManagerName || null,
    secondLineManagerInfinityCode: formData.secondLineManagerCode || null,
    secondLineManagerName: formData.secondLineManagerName || null,
    workingAgeYears: formData.workingAgeYears || null,
    workingAgeMonths: formData.workingAgeMonths || null,
    resignedDate: formData.resignedDate || null,
    resignReasons: formData.resignReasons || null,
    
    // Include client info
    clientName: formData.clientName || null,
    clientCode: formData.clientCode || null,
  };

  return dbPayload;
};

export const addEmployeeAPI = async (employeeData) => {
  // Map form fields to database schema
  const dbPayload = mapFormDataToDatabase(employeeData);

  const response = await apiFetch(`${API_URL}/employees`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(dbPayload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    const errMsg = errorData?.message || errorData?.error || "Failed to add employee";
    throw new Error(errMsg);
  }

  const data = await response.json();
  return data;
};
